import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, Sparkles, X, ArrowRight } from 'lucide-react';

export default function GetStartedModal({ isOpen, onClose }) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleChoice = (route) => {
    onClose();
    navigate(route);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="relative max-w-4xl w-full mx-4">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
        
        {/* Content */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 border-2 border-slate-700">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">
              Choose Your Starting Point
            </h2>
            <p className="text-slate-400">
              Pick the AI tool that fits your current need
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Market Intelligence Card */}
            <Card className="group bg-slate-800/50 border-2 border-blue-500/30 hover:border-blue-500 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/20">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white group-hover:text-cyan-400 transition-colors">
                  Market Intelligence
                </CardTitle>
                <CardDescription className="text-slate-400 text-base">
                  Get instant insights on salary, market trends, and talent availability for any role
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6 text-sm text-slate-300">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    Salary benchmarks & compensation
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    10 matching candidate profiles
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    Sourcing strategies & email templates
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    Discovery questions for hiring managers
                  </li>
                </ul>
                <Button
                  onClick={() => handleChoice('/market-intelligence')}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white group-hover:shadow-lg group-hover:shadow-blue-500/30"
                >
                  Start with Market Intelligence
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            {/* SmartMatch Card */}
            <Card className="group bg-slate-800/50 border-2 border-purple-500/30 hover:border-purple-500 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/20">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white group-hover:text-purple-400 transition-colors">
                  SmartMatch
                </CardTitle>
                <CardDescription className="text-slate-400 text-base">
                  AI-powered candidate scoring with instant job fit analysis and rankings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6 text-sm text-slate-300">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Score up to 5 candidates (0-10)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Technical & cultural fit analysis
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Resume & LinkedIn support
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                    Instant rankings & recommendations
                  </li>
                </ul>
                <Button
                  onClick={() => handleChoice('/smartmatch')}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white group-hover:shadow-lg group-hover:shadow-purple-500/30"
                >
                  Start with SmartMatch
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-6">
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-sm transition-colors"
            >
              I'll explore on my own
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
